import React, { useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  alpha,
  useTheme,
  Fade,
  Alert,
} from "@mui/material";
import {
  LockOutlined as AccessDeniedIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";

// Sidor & Komponenter
import CurrentVoyage from "../pages/currentvoyage/CurrentVoyage";
import StatementOfFact from "../pages/statementoffact/StatementOfFact";
import { Checklists } from "../pages/checklists/Checklists";
import Account from "../pages/account/Account";
import Settings from "../pages/settings/Settings";
import NoticeOfReadiness from "../pages/noticeofreadiness/PrintNOR";
import CleanlinessTankCertification from "../pages/tankcert/PrintCTC";
import Slop from "../pages/slop/Slop";
import ReceivingCapacityStatement from "../pages/receivingcapacitystatement/PrintRCS";
import Logging from "../pages/pumplogging/Logging";
import PumpStatement from "../pages/pumpstatement/PrintPumpStatement";
import Protests from "../pages/protests/Protests";
import DocumentScanner from "../pages/DocumentScanner";
import FAQ from "../pages/faq/FAQ";
import ObqRob from "../pages/obqrob/ObqRob";
import EmptyTankCert from "../pages/tankcert/PrintETC";
import DeadweightStatement from "../pages/deadweightstatement/DeadweightStatement";
import CompletedVoyages from "../pages/completedvoyages/CompletedVoyages";
import BargeLogs from "../pages/bareglogs/BargeLogs";
import CargoTankStatus from "../pages/cargotankstatus/CargoTankStatus";
import Permits from "../pages/permits/Permits";
import ShipParticulars from "../pages/shipParticulars/shipParticulars";
import VoyageSelectBanner from "./VoyageSelectBanner";
import PrintableDocuments from "../pages/printabledocuments/PrintableDocuments";
import VesselExperienceFactor from "../pages/vef/VesselExperienceFactor";
import ThreeLastCargoes from "../pages/threelastcargoes/ThreeLastCargoes";
import DocumentArchive from "../pages/settings/DocumentArchive";

// Contexts
import { useUserContext } from "../Contexts/UserContext";
import { useVoyageContextV2 } from "../Contexts/VoyageContextV2"; // Uppdaterad till V2
import { hasVoyageTrips } from "../utils/voyageSelection";

const pagesWithoutVoyageRequirement = [
  "currentVoyage",
  "settings",
  "account",
  "faq",
  "completedvoyages",
  "checklists",
  "shipParticulars",
  "cargotankstatus",
  "permits",
  "vef",
  "threelastcargoes",
  "documentArchive",
];

const Content = ({ activeSection, open, isMobile, onSelect }) => {
  const theme = useTheme();
  const { currentVoyage, isVoyageReadOnly } = useVoyageContextV2(); // Använder V2
  const { user, setUserData, hasAccessToPage } = useUserContext();

  const isPageAllowedWithoutVoyage =
    pagesWithoutVoyageRequirement.includes(activeSection);

  useEffect(() => {
    if (!user) {
      setUserData({
        username: "tomte_gubben",
        email: "santa@north.pole",
        firstName: "Tomte",
        lastName: "Gubben",
        roles: ["admin", "user"],
      });
    }
  }, [user, setUserData]);

  // Dynamisk styling baserat på sidebar-läge och enhet
  const contentStyle = {
    marginLeft: isMobile ? 0 : open ? "280px" : "80px",
    marginTop: isMobile ? "10px" : "20px",
    transition: theme.transitions.create(["margin-left"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    padding: theme.spacing(3),
    minHeight: "calc(100vh - 40px)",
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  };

  // Helper för att rendera tomma/låsta tillstånd
  const FullscreenNotice = ({ title, subtitle, icon: Icon }) => (
    <Paper
      elevation={0}
      sx={{
        ...contentStyle,
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        bgcolor: "transparent",
      }}
    >
      <Box
        sx={{
          p: 6,
          borderRadius: 4,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(10px)",
          border: "1px solid",
          borderColor: "divider",
          maxWidth: 500,
        }}
      >
        <Icon
          sx={{ fontSize: 60, color: "primary.main", mb: 2, opacity: 0.5 }}
        />
        <Typography variant="h5" fontWeight="800" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );

  // 1. Säkerhetskoll: Har användaren behörighet?
  if (!hasAccessToPage(activeSection)) {
    return (
      <FullscreenNotice
        title="Access Denied"
        subtitle="Your current role does not have permission to view this operational section."
        icon={AccessDeniedIcon}
      />
    );
  }

  // 2. Logikkoll: Kräver sidan en aktiv voyage?
  const hasTripData = hasVoyageTrips(currentVoyage);
  const bannerLeft = isMobile
    ? theme.spacing(2)
    : open
      ? `calc(280px + ${theme.spacing(3)})`
      : `calc(80px + ${theme.spacing(3)})`;
  const bannerRight = theme.spacing(3);
  const bannerHeight = 74;

  if (!isPageAllowedWithoutVoyage && !hasTripData) {
    return (
      <FullscreenNotice
        title="No Active Voyage"
        subtitle="Please initialize a new voyage or select an existing one under 'Current Voyage' to access this document."
        icon={InfoIcon}
      />
    );
  }

  return (
    <Box sx={contentStyle}>
      {hasTripData && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: bannerLeft,
            right: bannerRight,
            zIndex: 1200,
            pt: isMobile ? 1 : 2,
          }}
        >
          <VoyageSelectBanner />
        </Box>
      )}

      <Fade in timeout={500}>
        <Box>
          {hasTripData && <Box sx={{ height: `${bannerHeight}px` }} />}

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: { xs: 2, md: 4 },
              borderRadius: 4,
              minHeight: "60vh",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            {isVoyageReadOnly && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Completed voyage loaded. Edit actions are disabled.
              </Alert>
            )}
            {(() => {
              switch (activeSection) {
                case "currentVoyage":
                  return <CurrentVoyage />;
                case "statementOfFact":
                  return <StatementOfFact user={user} />;
                case "nor":
                  return <NoticeOfReadiness user={user} />;
                case "ctc":
                  return <CleanlinessTankCertification user={user} />;
                case "slop":
                  return <Slop user={user} />;
                case "rcs":
                  return <ReceivingCapacityStatement user={user} />;
                case "logging":
                  return <Logging user={user} />;
                case "pumpstatement":
                  return <PumpStatement user={user} />;
                case "protests":
                  return <Protests user={user} />;
                case "checklists":
                  return <Checklists user={user} />;
                case "account":
                  return <Account user={user} />;
                case "settings":
                  return <Settings user={user} onSelect={onSelect} />;
                case "documentArchive":
                  return <DocumentArchive />;
                case "shipParticulars":
                  return <ShipParticulars user={user} />;
                case "pdfreader":
                  return <DocumentScanner />;
                case "faq":
                  return <FAQ />;
                case "obqrob":
                  return <ObqRob />;
                case "etc":
                  return <EmptyTankCert />;
                case "deadweight":
                  return <DeadweightStatement />;
                case "completedvoyages":
                  return <CompletedVoyages />;
                case "bargelogs":
                  return <BargeLogs />;
                case "cargotankstatus":
                  return <CargoTankStatus />;
                case "permits":
                  return <Permits />;
                case "printabledocuments":
                  return <PrintableDocuments />;
                case "vef":
                  return <VesselExperienceFactor />;
                case "threelastcargoes":
                  return <ThreeLastCargoes />;
                default:
                  return (
                    <Box sx={{ py: 10, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        Select a section from the command menu.
                      </Typography>
                    </Box>
                  );
              }
            })()}
          </Paper>
        </Box>
      </Fade>
    </Box>
  );
};

export default Content;
